/**
 * Chat settings — persisted in localStorage
 */

import { useState, useCallback } from 'react';

const STORAGE_KEY = 'vidnight_chat_settings';

export interface ChatSettings {
  notificationSound: boolean;
  desktopNotifications: boolean;
  messagePreview: boolean;
  chatVibration: boolean;
}

const DEFAULT_SETTINGS: ChatSettings = {
  notificationSound: true,
  desktopNotifications: true,
  messagePreview: true,
  chatVibration: true,
};

export function getChatSettings(): ChatSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_SETTINGS, ...JSON.parse(stored) };
    }
  } catch {
    // ignore parse errors
  }
  return { ...DEFAULT_SETTINGS };
}

function saveChatSettings(settings: ChatSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // ignore storage errors
  }
}

export function useChatSettings() {
  const [settings, setSettings] = useState<ChatSettings>(getChatSettings);

  const updateSetting = useCallback(<K extends keyof ChatSettings>(key: K, value: ChatSettings[K]) => {
    setSettings(prev => {
      const next = { ...prev, [key]: value };
      saveChatSettings(next);
      return next;
    });
  }, []);

  return { settings, updateSetting };
}
